/**
 * Hangman Engine — Core Game Logic
 *
 * Handles word selection, letter guessing, win/loss detection, and hints.
 *
 * Word selection algorithm:
 * 1. Filter word list by category (or use all categories)
 * 2. Filter by difficulty word-length range
 * 3. Pick a random word from the filtered list
 * 4. For easy difficulty, reveal the first letter as a hint
 */

import {
  HangmanGameState,
  HangmanDifficulty,
  HangmanCategory,
  HangmanGameSettings,
  MAX_WRONG_GUESSES,
  WORD_LISTS,
  DIFFICULTY_WORD_LENGTH,
} from './types';

export class HangmanEngine {
  private gameState: HangmanGameState | null = null;

  // =========================================================================
  // Public API
  // =========================================================================

  /** Initialize a new game with the given settings. */
  initializeGame(settings: HangmanGameSettings): HangmanGameState {
    const word = this.pickWord(settings.difficulty, settings.category);

    const initialGuessed: string[] = [];

    // Easy mode: reveal the first letter as a hint
    if (settings.difficulty === 'easy') {
      const firstLetter = word[0];
      if (!initialGuessed.includes(firstLetter)) {
        initialGuessed.push(firstLetter);
      }
    }

    this.gameState = {
      word,
      category: settings.category === 'all' ? this.getWordCategory(word) : settings.category,
      difficulty: settings.difficulty,
      guessedLetters: [...initialGuessed],
      correctLetters: [...initialGuessed],
      wrongLetters: [],
      wrongGuessCount: 0,
      gameStatus: 'playing',
      hintUsed: false,
      gameId: this.generateId(),
      startedAt: Date.now(),
      timer: 0,
    };

    return this.getGameState();
  }

  /** Get a deep copy of the current game state. */
  getGameState(): HangmanGameState {
    if (!this.gameState) {
      throw new Error('Game not initialized');
    }
    return JSON.parse(JSON.stringify(this.gameState));
  }

  /** Guess a letter. Returns updated game state. */
  guessLetter(letter: string): HangmanGameState {
    if (!this.gameState) throw new Error('Game not initialized');
    if (this.gameState.gameStatus !== 'playing') throw new Error('Game is not in progress');

    const upperLetter = letter.toUpperCase();

    if (upperLetter.length !== 1 || !/^[A-Z]$/.test(upperLetter)) {
      throw new Error('Must guess a single letter A-Z');
    }

    if (this.gameState.guessedLetters.includes(upperLetter)) {
      // Already guessed — no-op, return current state
      return this.getGameState();
    }

    this.gameState.guessedLetters.push(upperLetter);

    if (this.gameState.word.includes(upperLetter)) {
      // Correct guess
      this.gameState.correctLetters.push(upperLetter);
    } else {
      // Wrong guess
      this.gameState.wrongLetters.push(upperLetter);
      this.gameState.wrongGuessCount++;
    }

    // Check win/loss
    if (this.isGameWon()) {
      this.gameState.gameStatus = 'won';
    } else if (this.isGameLost()) {
      this.gameState.gameStatus = 'lost';
    }

    return this.getGameState();
  }

  /** Check if all letters in the word have been guessed. */
  isGameWon(): boolean {
    if (!this.gameState) return false;
    const uniqueLetters = new Set(this.gameState.word.split(''));
    return [...uniqueLetters].every((l) => this.gameState!.correctLetters.includes(l));
  }

  /** Check if max wrong guesses have been reached. */
  isGameLost(): boolean {
    if (!this.gameState) return false;
    return this.gameState.wrongGuessCount >= MAX_WRONG_GUESSES;
  }

  /** Reveal one unguessed letter as a hint. Returns the letter revealed, or null. */
  getHint(): { state: HangmanGameState; letter: string | null } {
    if (!this.gameState) throw new Error('Game not initialized');
    if (this.gameState.gameStatus !== 'playing') throw new Error('Game is not in progress');
    if (this.gameState.hintUsed) {
      return { state: this.getGameState(), letter: null };
    }

    // Find unguessed letters in the word
    const uniqueLetters = new Set(this.gameState.word.split(''));
    const unguessed = [...uniqueLetters].filter(
      (l) => !this.gameState!.guessedLetters.includes(l)
    );

    if (unguessed.length === 0) {
      return { state: this.getGameState(), letter: null };
    }

    // Pick a random unguessed letter
    const hintLetter = unguessed[Math.floor(Math.random() * unguessed.length)];

    this.gameState.guessedLetters.push(hintLetter);
    this.gameState.correctLetters.push(hintLetter);
    this.gameState.hintUsed = true;

    // Check win after hint
    if (this.isGameWon()) {
      this.gameState.gameStatus = 'won';
    }

    return { state: this.getGameState(), letter: hintLetter };
  }

  /** Get the word display: guessed letters shown, unguessed as underscores. */
  getWordDisplay(): string[] {
    if (!this.gameState) throw new Error('Game not initialized');
    return this.gameState.word.split('').map((letter) =>
      this.gameState!.guessedLetters.includes(letter) ? letter : '_'
    );
  }

  /** Update the timer value. */
  updateTimer(seconds: number): void {
    if (this.gameState) {
      this.gameState.timer = seconds;
    }
  }

  /** Serialize game state for persistence. */
  serialize(): string {
    return JSON.stringify(this.gameState);
  }

  /** Deserialize and restore game state. */
  deserialize(data: string): HangmanGameState {
    this.gameState = JSON.parse(data);
    return this.getGameState();
  }

  // =========================================================================
  // Word Selection
  // =========================================================================

  /** Pick a random word matching the difficulty and category. */
  private pickWord(difficulty: HangmanDifficulty, category: HangmanCategory): string {
    const { min, max } = DIFFICULTY_WORD_LENGTH[difficulty];

    let wordPool: string[];

    if (category === 'all') {
      // Combine all categories
      wordPool = Object.values(WORD_LISTS).flat();
    } else {
      wordPool = WORD_LISTS[category];
    }

    // Filter by word length for the difficulty
    const filtered = wordPool.filter((w) => w.length >= min && w.length <= max);

    if (filtered.length === 0) {
      // Fallback: use all words from the category if no words match the length
      if (wordPool.length > 0) {
        return wordPool[Math.floor(Math.random() * wordPool.length)];
      }
      // Ultimate fallback
      return 'HANGMAN';
    }

    return filtered[Math.floor(Math.random() * filtered.length)];
  }

  /** Determine which category a word belongs to (for 'all' mode). */
  private getWordCategory(word: string): HangmanCategory {
    for (const [cat, words] of Object.entries(WORD_LISTS)) {
      if (words.includes(word)) {
        return cat as HangmanCategory;
      }
    }
    return 'all';
  }

  // =========================================================================
  // Utility
  // =========================================================================

  private generateId(): string {
    return `hangman_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
  }
}
